import { useEffect, useState, useCallback } from 'react';

import type { AvailableSite } from '@atlassian/gic-anywhere';

import type { JiraProject } from '../__types__/apiUtils';
// TODO: move to __types__/apiUtils.ts or create a new file for single issue create form types
import type { IssueType } from '../providers/BulkCreateContextProvider';

import { fetchIssueTypes } from './apiUtils';

export const FETCH_ISSUE_TYPES = 'fetch-issue-types';

export type UseGetIssueTypesResult = {
	optionsIssueTypes: IssueType[];
	selectedIssueType: IssueType | null;
	isLoading: boolean;
	error: Error | null;
	onIssueTypeChange: (issueType: IssueType | null) => void;
};

export const useGetIssueTypesForSingleIssueCreateForm = (
	selectedAvailableSite: AvailableSite | null = null,
	selectedProject: JiraProject | null = null,
): UseGetIssueTypesResult => {
	const [optionsIssueTypes, setOptionsIssueTypes] = useState<IssueType[]>([]);
	const [selectedIssueType, setSelectedIssueType] = useState<IssueType | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<Error | null>(null);

	const resetState = useCallback(() => {
		setOptionsIssueTypes([]);
		setSelectedIssueType(null);
		setError(null);
	}, []);

	// Reset state when project changes
	useEffect(() => {
		resetState();
	}, [selectedProject?.id, resetState]);

	// Fetch issue types when dependencies are available
	useEffect(() => {
		const cloudId = selectedAvailableSite?.cloudId;
		const projectId = selectedProject?.id;

		if (!cloudId || !projectId) {
			return;
		}

		const fetchIssues = async () => {
			setIsLoading(true);
			setError(null);

			try {
				const data = await fetchIssueTypes({ cloudId, projectId });
				const validIssueTypes = data?.filter((issueType) => !issueType.subtask) || [];
				setOptionsIssueTypes(validIssueTypes);

				if (validIssueTypes.length > 0) {
					setSelectedIssueType(validIssueTypes[0]);
				}
			} catch (e: Error | unknown) {
				resetState();
				setError(e as Error);
			} finally {
				setIsLoading(false);
			}
		};

		void fetchIssues();
	}, [selectedAvailableSite?.cloudId, selectedProject?.id, resetState]);

	const handleIssueTypeChange = useCallback((issueType: IssueType | null) => {
		setSelectedIssueType(issueType);
	}, []);

	return {
		optionsIssueTypes,
		selectedIssueType,
		isLoading,
		error,
		onIssueTypeChange: handleIssueTypeChange,
	};
};
